import { type MarkedToken, type Tokens } from 'marked';

import { createMarkdownNativeMarked } from './sections/section-markdown';

/**
 * HTML-escape for attribute values. Package-owned rather than imported: the
 * app reached for lodash's `escape`, but `lodash-es` is ESM-only and pulling it
 * into the kernel would add an undeclared runtime dependency plus a transform
 * carve-out in every consumer's Jest project. Semantics are identical to
 * lodash's `escape` (verified exhaustively over the first 768 code points):
 * `&`, `<`, `>`, `"` and `'` become entities; nothing else is touched.
 */
const HTML_ESCAPES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

const escapeHtml = (value: string): string =>
  value.replace(/[&<>"']/g, (char) => HTML_ESCAPES[char] as string);

// A fresh instance, isolated from the editor kit's marked, which carries
// section/table node tokens (that one renders to ProseMirror JSON and throws
// "Token sectionNode not found" rendering HTML). This clean instance tolerates
// the partial input a stream produces (the canonical markdown setContent
// throws on it) and shares the ATX-only heading rule and html-block section
// boundary, so streamed and finalized parses agree.
const streamingMarked = createMarkdownNativeMarked();

// Plain text of marked inline tokens, mark syntax stripped — mirrors the
// canonical section parse's `inlineText(sectionTitle)`, so the streamed
// `data-section-title` equals the finalized one (`# **Plan**` → `Plan`).
// Typed over `MarkedToken` (not `Token`) to exclude `Tokens.Generic`, whose
// `any` index signature would make `token.text` an unsafe return.
const inlineTokenText = (tokens: MarkedToken[]): string =>
  tokens
    .map((token) =>
      'tokens' in token && token.tokens
        ? inlineTokenText(token.tokens as MarkedToken[])
        : 'text' in token
          ? token.text
          : ''
    )
    .join('');

/**
 * Render partial stream markdown to the editor's section HTML so `# ` headings
 * show section chrome (not a bare H1) while streaming — matching the finalized
 * note. Each `# ` opens a section (the markdown-native product convention);
 * body blocks are re-parsed from the summed token `raw`, so all grammar stays
 * marked's. Content before the first `# ` renders as a preamble (no section).
 */
export const renderStreamingMarkdownToHtml = (markdown: string): string => {
  // Cast away marked's `Tokens.Generic` union member (its index signature is
  // `any`, which defeats narrowing on `token.type`).
  const tokens = streamingMarked.lexer(markdown) as MarkedToken[];
  let current: { heading: Tokens.Heading | null; bodyRaw: string } = {
    heading: null,
    bodyRaw: '',
  };
  const groups = [current];
  for (const token of tokens) {
    if (token.type === 'heading' && token.depth === 1) {
      current = { heading: token, bodyRaw: '' };
      groups.push(current);
    } else {
      current.bodyRaw += token.raw;
    }
  }
  return groups
    .map(({ heading, bodyRaw }) => {
      const bodyHtml = bodyRaw.trim()
        ? streamingMarked.parse(bodyRaw, { async: false })
        : '';
      if (heading === null) return bodyHtml;
      // Render the heading's inline markdown so a styled title (`# **Plan**`)
      // shows marks while streaming — matching the canonical finalize — instead
      // of literal `**Plan**`. The attribute mirrors the finalize's plain-text
      // `sectionTitle` (marks stripped).
      const titleHtml = streamingMarked.parseInline(heading.text, { async: false });
      const titleAttr = escapeHtml(inlineTokenText(heading.tokens as MarkedToken[]));
      return `<section data-section-title="${titleAttr}"><h1>${titleHtml}</h1>${bodyHtml}</section>`;
    })
    .join('');
};
